import React, { useState, useEffect } from 'react';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle, Wand2, TrendingUp, Target, Zap } from 'lucide-react';
import { generatePostVariations, analyzePost } from '../../../services/aiPostGenerator';
import { generatePostVariations as generateGeminiPostVariations, analyzePost as analyzeGeminiPost } from '../../../services/geminiPostGenerator';
import { generatePostVariations as generateAnthropicPostVariations, analyzePost as analyzeAnthropicPost } from '../../../services/anthropicPostGenerator';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const AIPostGenerator = ({ onPostGenerated }) => {
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('general');
  const [tone, setTone] = useState('professional');
  const [keywords, setKeywords] = useState('');
  const [generatedPosts, setGeneratedPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [aiProvider, setAiProvider] = useState('openai');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandVoice, setBrandVoice] = useState(null);

  // Platform options for Select component
  const platformOptions = [
    { value: 'general', label: 'General' },
    { value: 'twitter', label: 'Twitter/X' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' }
  ];

  // Tone options for Select component
  const toneOptions = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'humorous', label: 'Humorous' },
    { value: 'inspirational', label: 'Inspirational' },
    { value: 'educational', label: 'Educational' }
  ];

  // Fetch user's brands for brand voice consistency
  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const { data, error } = await supabase
        ?.from('brands')
        ?.select('id, name, brand_voice_description, brand_values')
        ?.eq('user_id', user?.id)
        ?.eq('is_active', true);

      if (error) throw error;
      setBrands(data || []);

      // Auto-select first brand if available
      if (data?.length > 0) {
        handleBrandSelect(data?.[0]);
      }
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    if (brand?.brand_voice_description || brand?.brand_values) {
      setBrandVoice({
        description: brand?.brand_voice_description,
        values: brand?.brand_values || [],
        dosList: [],
        dontsList: []
      });
    } else {
      setBrandVoice(null);
    }
  };

  // Convert brands to options format for Select component
  const brandOptions = [
    { value: '', label: 'No brand voice' },
    ...brands?.map(brand => ({
      value: brand?.id,
      label: brand?.name
    }))
  ];

  const handleGenerate = async () => {
    if (!topic?.trim()) {
      setError('Please enter a topic for your post');
      return;
    }

    try {
      setIsGenerating(true);
      setError('');
      
      const options = {
        topic,
        tone,
        platform,
        maxLength: 280,
        keywords: keywords?.split(',')?.map(k => k?.trim())?.filter(Boolean),
        brandVoice: aiProvider === 'anthropic' ? brandVoice : undefined
      };

      let results;
      // Use selected AI provider
      if (aiProvider === 'gemini') {
        results = await generateGeminiPostVariations(options, 3);
      } else if (aiProvider === 'anthropic') {
        results = await generateAnthropicPostVariations(options, 3);
      } else {
        results = await generatePostVariations(options, 3);
      }
      
      setGeneratedPosts(results);
    } catch (err) {
      setError(err?.message || 'Failed to generate posts');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnalyze = async (post) => {
    try {
      setIsAnalyzing(true);
      setError('');
      
      let analysis;
      // Use selected AI provider for analysis
      if (aiProvider === 'gemini') {
        analysis = await analyzeGeminiPost(post?.content, platform);
      } else if (aiProvider === 'anthropic') {
        analysis = await analyzeAnthropicPost(post?.content, platform, brandVoice);
      } else {
        analysis = await analyzePost(post?.content, platform);
      }
      
      setAnalysis(analysis);
    } catch (err) {
      setError(err?.message || 'Failed to analyze post');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSelectPost = (post) => {
    setSelectedPost(post);
    setAnalysis(null);
  };

  const handleUsePost = () => {
    if (selectedPost && onPostGenerated) {
      onPostGenerated(selectedPost?.content);
      // Reset state
      setTopic('');
      setKeywords('');
      setGeneratedPosts([]);
      setSelectedPost(null);
      setAnalysis(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">AI Post Generator</h3>
          <Sparkles className="w-5 h-5 text-purple-600" />
        </div>

        {/* AI Provider Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            AI Provider
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => setAiProvider('openai')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                aiProvider === 'openai' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">OpenAI (GPT-5)</span>
            </button>
            <button
              onClick={() => setAiProvider('gemini')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                aiProvider === 'gemini' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">Gemini 2.5</span>
            </button>
            <button
              onClick={() => setAiProvider('anthropic')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                aiProvider === 'anthropic' ? 'border-orange-600 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-orange-300'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">Claude Sonnet 4.5</span>
            </button>
          </div>
        </div>

        {/* Brand Voice Selection (Anthropic only) */}
        {aiProvider === 'anthropic' && brands?.length > 0 && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand Voice (Optional)
            </label>
            <Select
              value={selectedBrand?.id || ''}
              onChange={(e) => {
                const brand = brands?.find(b => b?.id === e?.target?.value);
                handleBrandSelect(brand);
              }}
              options={brandOptions}
            />
            {brandVoice && (
              <p className="text-xs text-gray-600 mt-2">
                ✓ Brand voice will be applied for consistency
              </p>
            )}
          </div>
        )}

        {/* Input Section */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Topic or Theme
            </label>
            <Input
              placeholder="e.g., Product launch, Industry insights, Tips..."
              value={topic}
              onChange={(e) => setTopic(e?.target?.value)}
              disabled={isGenerating}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform
              </label>
              <Select
                value={platform}
                onChange={(e) => setPlatform(e?.target?.value)}
                disabled={isGenerating}
                options={platformOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tone
              </label>
              <Select
                value={tone}
                onChange={(e) => setTone(e?.target?.value)}
                disabled={isGenerating}
                options={toneOptions}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keywords (optional, comma-separated)
            </label>
            <Input
              placeholder="e.g., innovation, growth, success"
              value={keywords}
              onChange={(e) => setKeywords(e?.target?.value)}
              disabled={isGenerating}
            />
          </div>

          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !topic?.trim()}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Generating Posts...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                Generate 3 Post Variations
              </>
            )}
          </Button>
        </div>
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        {/* Generated Posts */}
        {generatedPosts?.length > 0 && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Generated Variations:</h4>
            <div className="space-y-3">
              {generatedPosts?.map((post, index) => (
                <div
                  key={index}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPost === post
                      ? 'border-purple-500 bg-purple-50' :'border-gray-200 hover:border-purple-300'
                  }`}
                  onClick={() => handleSelectPost(post)}
                >
                  <p className="text-sm text-gray-800 whitespace-pre-wrap mb-2">
                    {post?.content}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{post?.metadata?.characterCount} characters</span>
                    {selectedPost === post && (
                      <CheckCircle className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                  
                  {/* Performance Predictions (Anthropic only) */}
                  {post?.performancePrediction && aiProvider === 'anthropic' && (
                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-gray-700">Performance Prediction</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1">
                          <Target className="w-3 h-3 text-blue-600" />
                          <span className="text-gray-600">Engagement: {post?.performancePrediction?.engagementScore}/100</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Zap className="w-3 h-3 text-yellow-600" />
                          <span className="text-gray-600">Virality: {post?.performancePrediction?.viralityPotential}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">
                        <strong>Best time:</strong> {post?.performancePrediction?.bestTimeToPost}
                      </p>
                      {post?.platformOptimizations?.hashtagStrategy && (
                        <p className="text-xs text-gray-600">
                          <strong>Hashtags:</strong> {post?.platformOptimizations?.hashtagStrategy}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Analysis Section */}
        {selectedPost && (
          <div className="mt-6 space-y-4">
            <Button
              onClick={() => handleAnalyze(selectedPost)}
              disabled={isAnalyzing}
              variant="outline"
              className="w-full"
            >
              {isAnalyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                'Analyze This Post'
              )}
            </Button>

            {analysis && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Strengths:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {analysis?.strengths?.map((strength, idx) => (
                      <li key={idx}>• {strength}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Suggestions:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {analysis?.suggestions?.map((suggestion, idx) => (
                      <li key={idx}>• {suggestion}</li>
                    ))}
                  </ul>
                </div>
                <div className="pt-2 border-t border-blue-200">
                  <p className="text-sm text-gray-600">
                    Quality Score: <span className="font-semibold">{analysis?.overallScore}/10</span>
                  </p>
                </div>
              </div>
            )}

            <Button
              onClick={handleUsePost}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              Use This Post
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIPostGenerator;